<script setup lang="ts">
import { ref, computed, inject, onMounted } from "vue";
import { CompareService } from "../services/compare.service";
import { SITEURL, BASEURL } from "@libTs/cms.globals";
import { itemCodeToUrlSlug } from "../helpers/url.helper";
import { vClickOutside } from "../directives/v-click-outside";
import { ProductsService } from "../services/products.service";
import { ItemService } from "../services/item.service";
import { WatchlistService } from "../services/watchlist.service";

const compareSvc = inject<CompareService>("compareSvc") as CompareService;
const productsSvc = inject<ProductsService>("productsSvc") as ProductsService;
const itemSvc = inject<ItemService>("itemSvc") as ItemService;
const watchlistSvc = inject<WatchlistService>(
    "watchlistSvc",
) as WatchlistService;

const { group } = defineProps({
    group: {
        type: String,
        required: true,
    },
});

const groupItems = ref([]);

// mounted
onMounted(() => {
    // Ensure the compare service has loaded items
    loadItemInfos();
});

const itemsInGroup = computed(() => {
    return compareSvc.list.filter((item) => {
        return (item.Group || "Ungrouped") === group;
    });
});

const loadItemInfos = async () => {
    let tempGroupItems = compareSvc.list.filter((item) => {
        return (item.Group || "Ungrouped") === group;
    });

    if (tempGroupItems.length > 0) {
        const itemCodes = tempGroupItems.map((item) => item.ItemCode);
        await productsSvc.getMultipleItemInfos(itemCodes).then((infos) => {
            // Update each item with the fetched info
            groupItems.value = tempGroupItems.map((item) => {
                return {
                    ...item,
                    ...infos[item.ItemCode],
                };
            });
        });

        // console.log("groupItems", groupItems);
    }
};

const addToWatchlist = (item) => {
    watchlistSvc.add({
        ItemCode: item.ItemCode,
        ItemName: item.ItemName,
        ImageURL: item.ImageURL ?? "",
    });
};

const removeFromComparison = (itemCode: string) => {
    compareSvc.remove({ ItemCode: itemCode });
    // Refresh the group items after removal
    loadItemInfos();
};

const scrollRight = () => {
    const container = document.querySelector(
        ".comparison-scroll-container",
    ) as HTMLElement;
    if (container) {
        container.scrollBy({ left: 300, behavior: "smooth" });
    }
};

const scrollLeft = () => {
    const container = document.querySelector(
        ".comparison-scroll-container",
    ) as HTMLElement;
    if (container) {
        container.scrollBy({ left: -300, behavior: "smooth" });
    }
};
</script>
<template>
    <div class="grid grid-cols-24">
        <div class="col-start-3 col-end-23">
            <div class="mb-12 flex justify-between items-center">
                <h2>{{ group }}</h2>
                <div class="flex flex-row gap-4">
                    <button
                        @click="scrollLeft()"
                        class="btn-cb h-8 w-8 p-0 flex items-center justify-center"
                    >
                        <i class="far fa-arrow-left"></i>
                    </button>
                    <button
                        @click="scrollRight()"
                        class="btn-cb h-8 w-8 p-0 flex items-center justify-center"
                    >
                        <i class="far fa-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>
        <div class="col-start-3 col-end-25">
            <div class="comparison-scroll-container">
                <table class="comparison-table" v-if="groupItems.length > 0">
                    <tbody>
                        <tr>
                            <td class="font-bold text-f5 !pr-16">Produkt</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            ></td>
                        </tr>
                        <tr>
                            <td></td>
                            <td
                                class="pr-4 h-2"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <div
                                    class="shadow bg-white rounded-m flex flex-col gap-8 px-5 pb-8 h-full justify-between"
                                >
                                    <div class="flex flex-col gap-8">
                                        <div
                                            class="flex flex-row justify-between"
                                        >
                                            <img
                                                :src="SITEURL + item.ImageURL"
                                                :alt="item.ItemName"
                                                class="h-28 w-40"
                                                v-if="item.ImageURL"
                                            />
                                            <div
                                                v-else
                                                class="h-28 w-40 flex items-center"
                                            >
                                                <img
                                                    :src="
                                                        BASEURL +
                                                        'assets/icons/no-photo.svg'
                                                    "
                                                    alt="No Image Available"
                                                    class="w-full h-1/3 object-contain"
                                                />
                                            </div>
                                            <div
                                                class="flex flex-row gap-4 mt-6"
                                            >
                                                <button
                                                    @click.stop="
                                                        addToWatchlist(item)
                                                    "
                                                    class="btn-pin-cb"
                                                ></button>

                                                <button
                                                    @click.stop="
                                                        removeFromComparison(
                                                            item.ItemCode,
                                                        )
                                                    "
                                                    class="btn-trash-cb"
                                                ></button>
                                            </div>
                                        </div>
                                        <div>
                                            <h3
                                                class="font-bold text-f4 break-word hyphens-auto"
                                            >
                                                {{ item.ItemName }}
                                            </h3>
                                        </div>
                                    </div>
                                    <div class="flex flex-col gap-8">
                                        <a
                                            class="underline"
                                            :href="
                                                '/actions/sapShop/products/brand-redirect?brand=' +
                                                item.brand
                                            "
                                        >
                                            {{ item.brand }}
                                        </a>
                                        <div class="">
                                            <div>Art. Nr.</div>
                                            <div class="font-bold">
                                                {{ item.ItemCode }}
                                            </div>
                                        </div>
                                        <div
                                            class="font-bold text-f4"
                                            v-if="item.Preis"
                                        >
                                            CHF
                                            {{
                                                (item.Preis as Number).toFixed(
                                                    2,
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <tr>
                            <td class="font-bold text-f5 !pr-16">
                                The main differences
                            </td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            ></td>
                        </tr>
                        <tr>
                            <td class="!pr-16">Packsize</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <span v-if="item.packsize">
                                    {{ item.packsize || "–" }}
                                </span>
                                <span v-else>–</span>
                            </td>
                        </tr>

                        <tr>
                            <td class="!pr-16">Description</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <span v-if="item.description">
                                    {{ item.description || "–" }}
                                </span>
                                <span v-else>–</span>
                            </td>
                        </tr>

                        <tr>
                            <td class="font-bold text-f5 !pr-16">
                                General information
                            </td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            ></td>
                        </tr>
                        <tr>
                            <td class="!pr-16">Manufacturer</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <span v-if="item.brand">
                                    {{ item.brand || "–" }}
                                </span>
                                <span v-else>–</span>
                            </td>
                        </tr>
                        <tr>
                            <td class="!pr-16">Category</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <span v-if="item.category">
                                    {{ item.category || "–" }}
                                </span>
                                <span v-else>–</span>
                            </td>
                        </tr>
                        <tr>
                            <td class="!pr-16">Availability</td>
                            <td
                                class="pr-4"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <span>
                                    {{
                                        itemSvc.availOrLeadText(
                                            item.availability,
                                            item.leadtime,
                                        ) || "–"
                                    }}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td></td>
                            <td
                                class="pr-4 pt-8"
                                v-for="item in groupItems"
                                :key="item.ItemCode"
                            >
                                <a
                                    :href="'/product/' + itemCodeToUrlSlug(item.ItemCode)"
                                    class="btn-cb"
                                    >Go to Product
                                    <i class="fas fa-arrow-right ml-2"></i>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
